import { Fragment, MouseEvent, ReactNode, useState, useEffect, memo, useCallback } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'

import Icon from '@/components/ui/icon'
import { useTenantContext } from '@/context/tenant'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar
} from '@/components/ui/sidebar'
import { getObjectFromStorage } from '@/utils/localStorage'
import { DomainConfig } from '@/config/domain'
import { ROUTES } from '@/config/routes'
import { NavGroups } from '@/hooks/use-sidebar-items'
import { ProductsMenuItem, isProductsMenuItem } from '@/features/products'
import { useAlerts } from '@/features/alerts/hooks/useAlerts'
import { Settings, Users } from 'lucide-react'
import { cn } from '@/lib/utils'

export type NavItems = {
  title: string
  url: string
  icon?: ReactNode
  isActive?: boolean
  items?: {
    title: string
    url: string
    icon?: ReactNode
    extras?: any
    items?: {
      title: string
      url: string
      icon?: ReactNode
    }[]
  }[]
  kind: string
}

// Memoizar o componente principal
// 1) RegularMenuItem: aceitar className e aplicar no SubButton
const RegularMenuItem = memo(
  ({
    subItem,
    state,
    kind,
    handleClickItem
  }: {
    subItem: any
    state: any
    kind: string
    handleClickItem: (event: MouseEvent, url?: string) => void
  }) => (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        asChild
        size="md"
        // `isActive` se você já souber
        className="flex items-center gap-2"
      >
        <NavLink
          to={subItem.url}
          state={kind === 'client' ? state : null}
          onClick={event => handleClickItem(event, subItem.url)}
          className={({ isActive }) => cn(isActive && 'data-[active=true]')}
        >
          {subItem.icon && <span className="shrink-0 size-4">{subItem.icon}</span>}
          <span className="truncate">{subItem.title}</span>
        </NavLink>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
)
RegularMenuItem.displayName = 'RegularMenuItem'

// 2) RenderSidebarItem: aceitar className e propagar
const RenderSidebarItem = memo(
  ({
    item,
    className,
    shouldBlockNavigation
  }: {
    item: NavItems
    className?: string
    shouldBlockNavigation: boolean
  }) => {
    const location = useLocation()
    const { state } = location
    const { open, openMobile, setOpenMobile, isMobile } = useSidebar()
    const navigate = useNavigate()

    const shouldClearClientState = useCallback((url?: string) => {
      if (!url) return false
      if (url === ROUTES.CLIENTS.path) return true
      return /^\/admin\/[^/]+\/clientes$/.test(url)
    }, [])

    const handleClickItem = useCallback(
      (event: MouseEvent, url?: string) => {
        event.preventDefault()

        if (shouldBlockNavigation && url && url !== '/alertas') {
          navigate('/alertas')
          if (isMobile && openMobile) {
            setOpenMobile(false)
          }
          return
        }

        if (url) {
          navigate(url, { state: shouldClearClientState(url) ? null : state })
        }
        if (!isMobile) return
        if (openMobile) {
          setOpenMobile(false)
        }
      },
      [isMobile, openMobile, setOpenMobile, navigate, state, shouldBlockNavigation, shouldClearClientState]
    )

    if (item.items && item.items.length) {
      return (
        <Collapsible key={item.title} asChild defaultOpen={item.isActive}>
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                tooltip={item.title}
                onClick={() => console.log('click', item.title, open, openMobile)}
                className={cn(
                  'group-data-[state=open]/collapsible:!bg-accent group-data-[state=open]/collapsible:!text-primary-foreground',
                  'hover:bg-accent hover:text-primary-foreground',
                  className
                )}
                data-state={open ? 'open' : 'closed'}
              >
                {item.icon && item.icon}
                <span>{item.title}</span>
                <Icon
                  name="ChevronRight"
                  className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                />
              </SidebarMenuButton>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <SidebarMenuSub
                className={cn('ml-3.5 mr-0  ', item.isActive ? 'animate-accordion-up' : 'animate-accordion-down')}
              >
                {item.items?.map(subItem => {
                  if (isProductsMenuItem(item.title)) {
                    return (
                      <ProductsMenuItem
                        key={`${subItem.title}-${subItem.url}`}
                        subItem={subItem}
                        keepOpen={item.items?.length === 1}
                        handleClickItem={() => {
                          navigate(shouldBlockNavigation ? '/alertas' : item.url, { state })
                        }}
                      />
                    )
                  }

                  return (
                    <RegularMenuItem
                      key={subItem.title}
                      subItem={subItem}
                      state={state}
                      kind={item.kind}
                      handleClickItem={handleClickItem}
                    />
                  )
                })}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      )
    }

    // Item sem filhos
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild className={cn(className)}>
          <Link
            to={item.url}
            state={item.kind === 'client' && !shouldClearClientState(item.url) ? state : null}
            onClick={event => handleClickItem(event, item.url)}
          >
            {item.icon && item.icon}
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }
)
RenderSidebarItem.displayName = 'RenderSidebarItem'

type NavMainProps = {
  groups: NavGroups[]
}

function getGroupIcon(title?: string, kind?: string) {
  // ajuste como quiser: por "kind" ou por "title"
  if ((title || '').toLowerCase().includes('admin')) return <Settings className="h-5 w-5" />
  if (kind === 'tenant' || (title || '').toLowerCase().includes('client')) return <Users className="h-5 w-5" />
  return <Users className="h-5 w-5" />
}

export function NavMain({ groups }: NavMainProps) {
  const navigate = useNavigate()
  const { selectedTenant, setSelectedTenant, setCurrentTenant } = useTenantContext()
  const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: boolean }>({})
  const { open } = useSidebar()
  const { shouldBlockNavigation } = useAlerts()

  useEffect(() => {
    if (selectedTenant) {
      setCollapsedGroups(prev => ({ ...prev, Admin: true }))
    }
  }, [selectedTenant])

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))
  }

  const handleCloseTenant = () => {
    if (shouldBlockNavigation) {
      navigate('/alertas')
      return
    }

    const principalTenant = getObjectFromStorage<DomainConfig>('currentTenant')
    setSelectedTenant(null)
    setCurrentTenant(principalTenant)
    setCollapsedGroups(prev => ({ ...prev, Admin: false }))
    navigate('/alertas')
  }

  return (
    <>
      {groups?.map(({ items, title, kind }, index) => {
        const groupKey = title || `group-${index}`
        const isCollapsed = collapsedGroups[groupKey] || false
        const isTenant = kind === 'tenant'
        const groupIcon = getGroupIcon(title, kind)

        return (
          <Fragment key={groupKey}>
            <SidebarGroup>
              <SidebarSeparator />

              {open ? (
                // === MODO ABERTO: label normal ===
                title && (
                  <SidebarGroupLabel
                    className="cursor-pointer hover:text-primary-foreground  hover:bg-primary
                               flex items-center justify-between transition-colors duration-200"
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <span className="w-full truncate">{title}</span>
                    <Icon
                      name={isCollapsed ? 'ChevronRight' : 'ChevronDown'}
                      className="h-4 w-4 transition-transform duration-200"
                    />
                  </SidebarGroupLabel>
                )
              ) : (
                // === MODO ICON: ícone centralizado clicável ===
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      tooltip={title}
                      onClick={() => toggleGroup(groupKey)}
                      className="h-9 w-9 mx-auto p-0 justify-center hover:bg-primary"
                    >
                      {groupIcon}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              )}

              {!isCollapsed && (
                <SidebarMenu>
                  {items.map((item, itemIndex) => (
                    <RenderSidebarItem
                      key={`${groupKey}-${item.title}-${item.url}-${itemIndex}`}
                      item={{ ...item, kind: kind ?? '' }}
                      className="hover:bg-accent hover:text-primary-foreground"
                      shouldBlockNavigation={shouldBlockNavigation}
                    />
                  ))}
                </SidebarMenu>
              )}
            </SidebarGroup>

            {isTenant && selectedTenant && (
              <SidebarMenuButton
                onClick={handleCloseTenant}
                // centraliza quando minimizado; texto quando aberto
                className={cn(
                  'hover:bg-red-800 hover:text-white text-sm w-full rounded-md font-medium mb-2 transition-colors duration-200',
                  open ? 'flex items-center justify-center gap-x-2 px-3 py-2' : 'h-9 w-9 mx-auto p-0 justify-center'
                )}
              >
                {open ? (
                  <>
                    <span>Fechar: {selectedTenant.title}</span>
                    <Icon name="X" className="h-4 w-4" />
                  </>
                ) : (
                  <Icon name="X" className="h-4 w-4" />
                )}
              </SidebarMenuButton>
            )}
          </Fragment>
        )
      })}
    </>
  )
}
